<template>
  <div id="search-form-filters-accordion">
    <div class="search-form-filters-accordion__trigger-wrapper">
      <b-button
        v-b-toggle.filters-accordion
        class="search-form-filters-accordion__trigger"
        variant="link"
      >
        <font-awesome-icon :icon="['far', 'sliders-h']" class="search-form-filters-accordion__trigger-icon" />
        <span class="search-form-filters-accordion__trigger-text">Need more filters?</span>
        <font-awesome-icon
          :icon="['far', isExpanded ? 'chevron-up' : 'chevron-down']"
          class="search-form-filters-accordion__trigger-chevron"
        />
      </b-button>
    </div>
    <b-collapse id="filters-accordion" @show="isExpanded = true" @hide="isExpanded = false">
      <div class="search-form-filters-accordion__content">
        <div
          v-if="filterTypeByArray(mostPopularFilterKey).length"
          :class="{ 'multi-column': totalOptions(filterTypeByArray(mostPopularFilterKey)) > 5 }"
          :style="columnStyle(totalOptions(filterTypeByArray(mostPopularFilterKey)))"
        >
          <JoinedSidebarSection
            :sections="filterTypeByArray(mostPopularFilterKey)"
            label="Most popular"
            :show-option-icons="true"
          />
        </div>
        <div
          v-if="filterTypeByArray(filterTypesAndStatus).length"
          :class="{ 'multi-column': totalOptions(filterTypeByArray(filterTypesAndStatus)) > 5 }"
          :style="columnStyle(totalOptions(filterTypeByArray(filterTypesAndStatus)))"
        >
          <JoinedSidebarSection
            :sections="filterTypeByArray(filterTypesAndStatus)"
            label="Type &amp; Status"
          />
        </div>
        <div
          v-for="(filterType, filterTypeIndex) in orderedFilterTypes"
          :key="filterTypeIndex"
          :class="{ 'multi-column': filterType.options && filterType.options.length > 5 }"
          :style="columnStyle(filterType.options ? filterType.options.length : 0)"
        >
          <SidebarSection :section="filterType" />
        </div>
      </div>
    </b-collapse>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import JoinedSidebarSection from '@/components/header/filters/sidebar/JoinedSidebarSection'
import SidebarSection from '@/components/header/filters/sidebar/SidebarSection'

export default {
  name: 'SearchFormFiltersAccordion',

  components: {
    JoinedSidebarSection,
    SidebarSection,
  },

  data: () => ({
    mostPopularFilterKey: ['basic'],
    filterTypesAndStatus: ['garden_type', 'open_status', 'type_status_extras'],
    filterTypes: ['about_visit', 'garden_style', 'seasonal_features'],
    isExpanded: false,
  }),

  computed: {
    ...mapGetters('filter', ['filterTypeByArray', 'dynamicFilterSections']),

    orderedFilterTypes() {
      const sections = this.dynamicFilterSections || {}
      return this.filterTypes
        .map((key) => sections[key])
        .filter(Boolean)
    },
  },

  mounted() {
    // Serve cached filters when valid; refetch only when missing/stale/expired.
    // Fire-and-forget: a failed refresh keeps any cached sections on screen.
    this.ensureFilters().catch(() => {})
  },

  methods: {
    ...mapActions('filter', ['ensureFilters']),
    totalOptions(sections) {
      return sections.reduce((sum, s) => sum + (s.options ? s.options.length : 0), 0)
    },
    columnStyle(count) {
      if (count <= 5) return {}
      return { '--col-size': Math.ceil(count / 2) }
    },
  },
}
</script>

<style scoped lang="scss">
#search-form-filters-accordion {
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-radius: 8px;
  // No overflow: hidden — allow full height so parent .search-form-content can scroll

  .search-form-filters-accordion__trigger-wrapper {
    padding: 0;
  }

  .search-form-filters-accordion__trigger {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: 56px;
    padding: 12px;
    background-color: $color-search-section-bg;
    border: none;
    border-radius: 0;
    color: $color-text-dark;
    text-decoration: none;
    text-align: left;

    &:hover,
    &:focus {
      background-color: $color-search-section-bg;
      color: $color-text-dark;
      text-decoration: none;
      box-shadow: none;
    }

    &-icon {
      flex-shrink: 0;
      font-size: 18px;
      width: 14px;
    }

    &-text {
      flex: 1 1 0;
      font-family: "Gill Sans", "Inter", sans-serif;
      font-weight: 600;
      font-size: 16px;
      min-width: 0;
    }

    &-chevron {
      flex-shrink: 0;
      font-size: 18px;
      width: 18px;
    }
  }

  // Let expanded collapse use full content height so parent .search-form-content can scroll
  ::v-deep #filters-accordion.collapse.show {
    height: auto !important;
    overflow: visible;
  }

  .search-form-filters-accordion__content {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px 12px 16px;
    background-color: $color-search-section-bg;

    // Dividers between filter sections
    > div + div {
      border-top: 1px solid $color-grey-dark-20;
      padding-top: 12px;
    }

    // Section styling (SidebarSection / JoinedSidebarSection)
    ::v-deep .filter-section {
      padding: 0;
      margin: 0;
      border-bottom: none;

      .section-title {
        font-family: "Gill Sans", "Inter", sans-serif;
        font-weight: 600;
        font-size: 18px;
        color: #000;
        margin-bottom: 12px;
      }

      .option {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-top: 0;
        padding: 0;
        cursor: pointer;
        color: $color-text-dark;
        font-family: "Gill Sans", "Inter", sans-serif;
        font-size: 18px;

        &:not(.joined):first-child {
          margin-top: 0;
        }

        & + .option {
          margin-top: 12px;
        }

        .option-square {
          width: 24px;
          height: 24px;
          min-width: 24px;
          min-height: 24px;
          margin-right: 0;
          border: 1px solid $color-grey-dark-20;
          background-color: #fff;
          border-radius: 5px;

          &.selected {
            background-color: $color-yellow-main;
            border-color: $color-yellow-main;
          }
        }
      }
    }

    // Two-column grid for sections with >5 options
    .multi-column ::v-deep .filter-section {
      display: grid;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: auto repeat(var(--col-size), auto);
      grid-auto-flow: column;
      column-gap: 24px;
      row-gap: 12px;

      .section-title {
        grid-column: 1 / -1;
        margin-bottom: 0;
      }

      .option {
        margin-top: 0;

        & + .option {
          margin-top: 0;
        }
      }
    }
  }
}
</style>
